import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import InstallAppButton from "@/components/InstallAppButton";
import {
  Sparkles, QrCode, ShieldCheck, BadgePercent, MapPin, Star, ArrowRight,
  CheckCircle2, Wallet, ScanLine, Gift, Zap, Repeat,
} from "lucide-react";

/* Realistic Gen Z member portraits — How it works, Voices + CTA sections.
   Every key is a distinct face; don't reuse one across sections. */
export const PORTRAIT = {
  femaleStudent: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/5cf35caf573435eb0f8d2e88ee381c63ae9b951d7467f2aab8434e94cb48d9b6.jpeg",
  malePro: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/63eb7856855c8086799e8182c98def6ca502b9b86572f1d5dde6074293398d2b.jpeg",
  femaleTraveller: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/8862b01b1daed8467f761b70c0851a344381d1d82a9dae6328625c4eb6e72aef.jpeg",
  maleStudent: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/f10255fe114fad2be3f362c901127db0d252b048f2a3b97920dbcc8d261a3264.jpeg",
  femalePro: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/7a05d11c3f050e790aae5d492d150f9a0aa0f911d9cf67f078c1c61a38173fc3.jpeg",
  maleTraveller: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/19ae51bb884a52228c689dab72cddf8266ad4d52238dc830df6dc37a7489e8f4.jpeg",
  diya: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/556868ab5cc97832efc79d56f30605799a653571fd8b5df7ed7ecc373b8925cb.jpeg",
  priyaStudent: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/04151a3e25a034bbdf951135a6e7d9f21f27644595ba88d20ababf863207f4db.jpeg",
  aaravTraveler: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/8554ee19c4e83636de4c248ae06fec98ea8beeb7dff52bf6d18e0c27a934b1da.jpeg",
  ishaDesigner: "https://static.prod-images.emergentagent.com/jobs/3bbdecd1-25c5-49d2-9748-3d70d05039f0/images/14719ea3792020fb1ad80c97a637b83c5011b4d39f4abeaaf4137907672804c5.jpeg",
};

function Counter({ value, label, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <div className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-primary uppercase">
        {value?.toLocaleString?.() ?? value}
      </div>
      <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-accent mt-1.5">{label}</div>
    </div>
  );
}

/* Floating reward chip used in the hero composition */
function RewardChip({ emoji, label, sub, className = "" }) {
  return (
    <div className={`absolute bg-card border border-border rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3 ${className}`}>
      <div className="w-10 h-10 rounded-xl vp-gradient-cool grid place-items-center text-xl">{emoji}</div>
      <div className="leading-tight">
        <div className="font-heading font-extrabold text-sm text-primary">{label}</div>
        <div className="text-[11px] text-muted-foreground font-medium">{sub}</div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [stats, setStats] = useState({ guests: 0, businesses: 0, total_savings: 0 });
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/plans").then((r) => setPlans(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Nav */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 md:h-24 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-home-logo">
            <img src="/valuepool-logo.png" alt="Value Pool" className="h-12 md:h-14 w-auto" />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-heading font-extrabold text-xl md:text-2xl text-primary tracking-tight">Value Pool</span>
              <span className="text-[10px] md:text-[11px] uppercase tracking-[0.14em] font-semibold text-secondary mt-1">Pool of Smart Savings</span>
            </div>
          </Link>
          <nav className="ml-auto hidden md:flex items-center gap-7 text-sm font-medium text-muted-foreground">
            <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
            <a href="#rewards" className="hover:text-foreground transition-colors">Rewards</a>
            <a href="#plans" className="hover:text-foreground transition-colors">Plans</a>
            <a href="#voices" className="hover:text-foreground transition-colors">Voices</a>
            <Link to="/sales-partner/apply" className="hover:text-foreground transition-colors hidden lg:inline" data-testid="nav-sales-partner-btn">Become a Partner</Link>
            <Link to="/login" className="hover:text-foreground transition-colors" data-testid="nav-login-btn">Log in</Link>
          </nav>
          <div className="ml-auto md:ml-4 flex items-center gap-2">
            <Link to="/signup">
              <Button className="vp-gradient-warm text-white font-heading font-extrabold uppercase tracking-wider text-xs rounded-full px-5 shadow-lg shadow-secondary/30" data-testid="nav-join-btn">
                Get rewarded
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 md:pt-14 pb-12 md:pb-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <Badge variant="secondary" className="bg-secondary/10 text-secondary border-secondary/30 uppercase tracking-widest text-[10px] font-extrabold px-3 py-1 mb-6" data-testid="hero-badge">
              <Gift className="w-3 h-3 mr-1.5" /> New · Monthly rewards live
            </Badge>
            <h1 className="hero-title text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95]">
              Unlock<br />
              <span className="vp-gradient-text">Free Rewards</span><br />
              with Every Bill.
            </h1>
            <p className="mt-7 text-base md:text-lg max-w-xl body-text leading-relaxed">
              One pass → instant discounts at hostels, PGs, cafés, restaurants
              and snack shops near you. Show your dynamic QR, the partner scans,
              the savings drop straight into your pocket.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/signup?role=guest">
                <Button size="lg" className="vp-gradient-warm text-white font-heading font-extrabold uppercase tracking-wider text-sm rounded-full px-7 shadow-xl shadow-secondary/30 lift" data-testid="cta-join-guest">
                  Start saving free <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/signup?role=business">
                <Button size="lg" variant="outline" className="border-2 border-primary text-primary font-heading font-bold uppercase tracking-wider text-sm rounded-full px-6 hover:bg-primary hover:text-primary-foreground" data-testid="cta-register-business">
                  I'm a business
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
              <Counter testid="counter-guests" value={stats.guests || 0} label="Smart Savers" />
              <Counter testid="counter-businesses" value={stats.businesses || 0} label="Partner Outlets" />
              <Counter testid="counter-savings" value={`₹${Math.round(stats.total_savings || 0).toLocaleString('en-IN')}`} label="Saved by Members" />
            </div>
          </div>

          {/* Hero visual — logo + floating reward chips */}
          <div className="md:col-span-5 relative">
            <div className="aspect-square relative grid place-items-center rounded-full" style={{
              background: "radial-gradient(circle at 50% 40%, rgba(0,169,145,0.18) 0%, rgba(118,203,122,0.10) 40%, transparent 70%)",
            }}>
              <img src="/valuepool-logo.png" alt="" className="w-[78%] drop-shadow-2xl" style={{ filter: "drop-shadow(0 30px 60px rgba(2,67,131,.18))" }} />
              <RewardChip emoji="💰" label="15% off" sub="At hostels" className="top-6 right-2 md:right-0 rotate-[6deg]" />
              <RewardChip emoji="☕" label="Free latte" sub="Welcome perk" className="bottom-20 -left-2 md:-left-8 -rotate-[4deg]" />
              <RewardChip emoji="🎉" label="₹100 credit" sub="Referral bonus" className="-bottom-2 right-6 rotate-[3deg]" />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-card py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="secondary" className="bg-accent/10 text-accent border-accent/30 uppercase tracking-widest text-[10px] font-extrabold px-3 py-1 mb-4">
              Three Steps
            </Badge>
            <h2 className="text-3xl md:text-5xl font-extrabold">How Value Pool works</h2>
            <p className="body-text mt-4">No coupon hunting. No app-store deals. Pay your bill, save instantly.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Sign up with your phone", d: "Verify your mobile via OTP. Pick a monthly pass. No paperwork.", img: PORTRAIT.priyaStudent, who: "Priya", icon: ScanLine },
              { n: "02", t: "Show your dynamic QR", d: "At any partner — café, PG, hostel — flash the QR from your dashboard. Refreshes every 5 minutes.", img: PORTRAIT.aaravTraveler, who: "Aarav", icon: QrCode },
              { n: "03", t: "Save on the bill", d: "Partner scans, enters bill, discount + complementary perks auto-apply. Rate & repeat.", img: PORTRAIT.ishaDesigner, who: "Isha", icon: Gift },
            ].map((s) => (
              <article key={s.n} className="rounded-3xl border border-border bg-background p-7 lift" data-testid={`how-step-${s.n}`}>
                <div className="flex items-center gap-4 mb-5">
                  <div className="relative shrink-0">
                    <img src={s.img} alt={`${s.who}, Value Pool member`} loading="lazy" width={64} height={64}
                         className="w-16 h-16 rounded-full object-cover shadow-md ring-2 ring-card bg-muted"
                         data-testid={`how-avatar-${s.n}`} />
                    {/* Action badge clipped to avatar — shows the verb (scan / QR / gift) */}
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full vp-gradient-warm text-white grid place-items-center shadow-lg ring-2 ring-card">
                      <s.icon className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className="font-heading font-black text-4xl vp-gradient-text ml-auto tracking-tighter">{s.n}</div>
                </div>
                <h3 className="step-title text-lg mb-2">{s.t}</h3>
                <p className="body-text text-sm leading-relaxed">{s.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards (formerly Benefits) */}
      <section id="rewards" className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <Badge variant="secondary" className="bg-secondary/10 text-secondary border-secondary/30 uppercase tracking-widest text-[10px] font-extrabold px-3 py-1 mb-4">
              Why members love it
            </Badge>
            <h2 className="text-3xl md:text-5xl font-extrabold">More than<br /><span className="vp-gradient-text">just discounts.</span></h2>
            <p className="body-text mt-5 leading-relaxed">
              Value Pool stacks complementary perks (free welcome drinks, desserts,
              extra services) on top of cash discounts — and the more you visit,
              the more your pool grows.
            </p>
            <Link to="/signup?role=guest" className="inline-block mt-7">
              <Button className="vp-gradient-warm text-white font-heading font-extrabold uppercase tracking-wider text-sm rounded-full px-6 shadow-lg shadow-secondary/30">
                Pool your first reward
              </Button>
            </Link>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { t: "Near-me discovery", d: "Geo-locate partners around you in seconds with the live map.", icon: MapPin },
              { t: "Complementary perks", d: "Free drinks, desserts, welcome treats — on top of discounts.", icon: Gift },
              { t: "Smart cycle limits", d: "Fair-use built in. Caps reset on renewal, no surprises.", icon: Repeat },
              { t: "Verified guest badge", d: "KYC-approved members get priority treatment at partners.", icon: ShieldCheck },
              { t: "Dynamic QR", d: "Refreshes every 5 minutes — fraud-proof, instant validation.", icon: QrCode },
              { t: "Instant savings", d: "No coupon codes, no waiting. Discount lands at checkout.", icon: Zap },
            ].map((b) => (
              <div key={b.t} className="rounded-2xl border border-border bg-card p-5 lift" data-testid={`benefit-${b.t.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl vp-gradient-cool grid place-items-center mb-3 text-white">
                  <b.icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="step-title text-base">{b.t}</div>
                <div className="body-text text-sm mt-1.5 leading-snug">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="bg-card py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Badge variant="secondary" className="bg-accent/10 text-accent border-accent/30 uppercase tracking-widest text-[10px] font-extrabold px-3 py-1 mb-4">
              Membership
            </Badge>
            <h2 className="text-3xl md:text-5xl font-extrabold">More Rewards. More Savings. <span className="vp-gradient-text">More Life.</span></h2>
            <p className="body-text mt-4">Switch, cancel or upgrade anytime. Members typically recover the fee in two outings.</p>
          </div>
          <div className={`grid gap-5 mx-auto ${plans.length === 1 ? "grid-cols-1 max-w-md" : "md:grid-cols-2 max-w-3xl"}`}>
            {plans.map((p, idx) => (
              <div key={p.id} className={`rounded-3xl border-2 p-7 bg-background lift relative ${idx === 1 ? "border-secondary ring-4 ring-secondary/10" : "border-border"}`} data-testid={`plan-${p.id}`}>
                {idx === 1 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 vp-gradient-warm text-white text-[10px] font-heading font-extrabold uppercase tracking-widest px-3 py-1 rounded-full shadow-lg">
                    Best value
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="step-title text-xl">{p.name}</div>
                  <Sparkles className="w-5 h-5 text-secondary" />
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <div className="font-heading text-5xl font-black text-primary">₹{p.price}</div>
                  <div className="text-muted-foreground text-sm">/ {p.period}</div>
                </div>
                <ul className="mt-5 space-y-2.5 text-sm body-text">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup?role=guest" className="block mt-7">
                  <Button className="w-full vp-gradient-warm text-white font-heading font-extrabold uppercase tracking-wider text-sm rounded-full" data-testid={`plan-cta-${p.id}`}>Start saving</Button>
                </Link>
              </div>
            ))}
            {plans.length === 0 && <div className="text-center text-muted-foreground">Loading plans…</div>}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="voices" className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Badge variant="secondary" className="bg-secondary/10 text-secondary border-secondary/30 uppercase tracking-widest text-[10px] font-extrabold px-3 py-1 mb-4">
            Voices from the pool
          </Badge>
          <h2 className="text-3xl md:text-5xl font-extrabold">Real members. <span className="vp-gradient-text">Real savings.</span></h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { name: "Aarav M.", role: "Bangalore · Student", img: PORTRAIT.maleStudent,
              q: "Paid for my PG with the money I saved on lunch. Genuinely — Value Pool pays for itself in two outings." },
            { name: "Isha I.", role: "Mumbai · Designer", img: PORTRAIT.femalePro,
              q: "Show QR, save 15%, get a free dessert. It's basically gamified eating out." },
            { name: "Meera N.", role: "Pune · Sales", img: PORTRAIT.femaleTraveller,
              q: "I travel for work. The hostel discounts alone covered my plan in week one." },
          ].map((t) => (
            <figure key={t.name} className="rounded-3xl border border-border bg-card p-6 lift" data-testid={`voice-${t.name.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center gap-3 mb-4">
                <img src={t.img} alt={t.name} loading="lazy" width={56} height={56}
                     className="w-14 h-14 shrink-0 rounded-full object-cover shadow-md ring-2 ring-card bg-muted"
                     data-testid={`voice-avatar-${t.name.split(" ")[0].toLowerCase()}`} />
                <div>
                  <div className="step-title text-sm">{t.name}</div>
                  <div className="text-xs text-accent font-semibold">{t.role}</div>
                </div>
                <div className="ml-auto flex items-center gap-0.5 text-secondary">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                </div>
              </div>
              <blockquote className="body-text text-sm leading-relaxed">"{t.q}"</blockquote>
            </figure>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-14">
        <div className="rounded-3xl p-8 md:p-14 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #024383 0%, #0A6CB8 100%)" }}>
          <div className="absolute inset-0 opacity-25 pointer-events-none" style={{
            background: "radial-gradient(circle at 90% 20%, #F89A3D 0%, transparent 35%), radial-gradient(circle at 10% 90%, #00A991 0%, transparent 35%)",
          }} />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h2 className="text-3xl md:text-5xl font-extrabold uppercase tracking-tight leading-[1.02]">
                Stop hunting<br />coupons. <span className="text-secondary">Start pooling.</span>
              </h2>
              <p className="mt-4 text-white/85 max-w-xl">
                Join thousands of smart savers and partner businesses across India.
                Your next free latte is one signup away.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/signup?role=guest"><Button size="lg" className="vp-gradient-warm text-white font-heading font-extrabold uppercase tracking-wider text-sm rounded-full px-7 shadow-xl shadow-black/20" data-testid="footer-cta-guest">Get started · free trial</Button></Link>
                <Link to="/signup?role=business"><Button size="lg" variant="outline" className="bg-transparent border-2 border-white/50 text-white hover:bg-white/10 font-heading font-bold uppercase tracking-wider text-sm rounded-full px-6" data-testid="footer-cta-business">I'm a business</Button></Link>
                <Link to="/sales-partner/apply"><Button size="lg" variant="outline" className="bg-transparent border-2 border-white/50 text-white hover:bg-white/10 font-heading font-bold uppercase tracking-wider text-sm rounded-full px-6" data-testid="footer-cta-sales">Sales partner</Button></Link>
              </div>
            </div>
            <div className="md:col-span-4 flex justify-center md:justify-end">
              {/* Community avatar cluster — represents real guests pooling savings together */}
              <div className="relative w-56 h-56 md:w-64 md:h-64">
                <div className="absolute inset-0 rounded-full bg-white/8 backdrop-blur-sm border border-white/15" />
                {[
                  { name: "Rohan",  img: PORTRAIT.malePro,       pos: "top-2 left-4",    size: "w-20 h-20", z: 30 },
                  { name: "Anaya",  img: PORTRAIT.femaleStudent, pos: "top-8 right-3",   size: "w-24 h-24", z: 40 },
                  { name: "Vikram", img: PORTRAIT.maleTraveller, pos: "bottom-6 left-2", size: "w-20 h-20", z: 30 },
                  { name: "Diya",   img: PORTRAIT.diya,     pos: "bottom-2 right-6", size: "w-24 h-24", z: 40 },
                ].map((c) => (
                  <img
                    key={c.name}
                    src={c.img}
                    alt={`${c.name}, Value Pool member`}
                    loading="lazy"
                    data-testid={`cta-avatar-${c.name.toLowerCase()}`}
                    className={`absolute ${c.pos} ${c.size} rounded-full object-cover ring-4 ring-white/40 shadow-2xl bg-white`}
                    style={{ zIndex: c.z }}
                  />
                ))}
                {/* tiny "+1.2k" badge for community feel */}
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-secondary text-white text-[10px] font-heading font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg z-50">
                  +12k saving
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-7 text-sm text-muted-foreground">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row gap-3 items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/valuepool-logo.png" alt="Value Pool" className="h-8 w-auto" />
            <span className="font-heading font-extrabold text-primary">Value Pool</span>
            <span className="text-xs">· Pool of Smart Savings · 2026</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="#plans" className="hover:text-foreground">Plans</a>
            <Link to="/login" className="hover:text-foreground">Log in</Link>
            <Link to="/signup" className="hover:text-foreground">Sign up</Link>
          </div>
        </div>
      </footer>

      <InstallAppButton />
    </div>
  );
}
